import type { ReactNode } from 'react';
import { AlertDialog as BaseAlertDialog } from '@base-ui/react';
import { Button } from '@clay-run/glaze/button';
import { Text } from '@clay-run/glaze/text';
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogBody,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@clay-run/glaze/alert-dialog';

/**
 * Plan `playbooks-clay-wire-gaps`, Unit U3 (review finding A4) — a
 * CONTROLLED confirmation dialog (`open`/`onOpenChange` props), deliberately
 * NOT Glaze's `useAlertDialog()` hook.
 *
 * WHY: `useAlertDialog`'s `content` is a JSX snapshot taken ONCE, at the
 * `openAlertDialog` call (verified against source —
 * `node_modules/@clay-run/glaze/dist/DialogBase-*.js`'s `setOpenModalArgs`
 * runs inside a one-time `setTimeout` when the dialog opens, not on every
 * render of the caller). Any state read inside that snapshot — a mutation's
 * `isPending`, a live count — is frozen at open time. For a plain
 * point-in-time confirmation (e.g. `SequenceMappingDrawer`'s sequence-swap
 * warning) that's fine: the dialog is confirm-or-cancel immediately and
 * nothing about it needs to update while it's open. It is NOT fine here:
 * this dialog stays open THROUGH an async mutation, so Cancel needs to
 * become disabled and Confirm needs to show a loading state the instant the
 * mutation starts — updates the one-time snapshot can never deliver. Same
 * reasoning as `PlayQueueCard.tsx`'s `GroupActionConfirmDialog` (controlled
 * `AlertDialog` for the same "hook needs a live re-render" reason), but
 * pushed one step further: that component still lets Cancel fire during a
 * pending confirm, which is the exact double-submit hole this component
 * closes for the Settings page's Save/Clear switch.
 *
 * Generic on purpose (no settings-specific copy baked in): a later unit
 * reuses this for per-row "Wipe log" / "Reset to draft" confirms on the
 * same Settings page.
 */
export interface ConfirmActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description: ReactNode;
  confirmLabel: ReactNode;
  cancelLabel?: ReactNode;
  /** @default 'primary' */
  variant?: 'danger' | 'primary';
  /**
   * True while the confirmed action's mutation is in flight. While pending:
   * Cancel is disabled, Confirm shows a loading state (and is disabled, so a
   * second click can't fire a second mutation), and any attempt to close the
   * dialog (Escape, backdrop click, the header close button) is ignored —
   * `onOpenChange(false)` simply does not propagate to the caller.
   */
  pending: boolean;
  onConfirm: () => void;
}

export function ConfirmActionDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  variant = 'primary',
  pending,
  onConfirm,
}: ConfirmActionDialogProps): ReactNode {
  function handleOpenChange(next: boolean) {
    // Ignore every close attempt (Escape, backdrop, header close button)
    // while a confirm is in flight — this is what actually prevents the
    // double-click double-submit the one-time snapshot let through.
    if (pending) return;
    onOpenChange(next);
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogHeader onClose={pending ? undefined : () => onOpenChange(false)}>
        <AlertDialogTitle>{title}</AlertDialogTitle>
      </AlertDialogHeader>
      <AlertDialogBody>
        {/*
         * Codex review finding F4 — Glaze's own `AlertDialogDescription`
         * (`node_modules/@clay-run/glaze/dist/DialogBase-*.js`) renders a
         * plain `Text`, never Base UI's `AlertDialog.Description` — so it
         * never registers a `descriptionElementId` and the dialog's
         * `aria-describedby` (which Base UI's `AlertDialog.Popup` DOES read
         * off that id — verified against
         * `node_modules/@base-ui/react/dialog/popup/DialogPopup.js`) is
         * simply absent. A screen reader announces the title but never this
         * warning copy. Fixed by rendering the REAL
         * `@base-ui/react` `AlertDialog.Description` (a `<p>` that calls
         * `useBaseUiId` + registers itself on the shared dialog store — same
         * package Glaze's own `AlertDialog.Root`/`Popup`/`Title` already come
         * from, so it's the same React context) as the actual DOM node,
         * styled via `Text`'s `render` prop (same composition Glaze itself
         * uses internally, and the same pattern already used at
         * `PlayQueueCard.tsx`'s `<Text render={<span />}>`) rather than
         * nesting the other way, which would just re-wrap Glaze's
         * non-registering `Text` and reintroduce the bug.
         */}
        <Text
          variant="tertiary"
          size="sm"
          className="text-pretty"
          render={<BaseAlertDialog.Description />}
        >
          {description}
        </Text>
      </AlertDialogBody>
      <AlertDialogActions>
        <Button variant="secondary" disabled={pending} onClick={() => handleOpenChange(false)}>
          {cancelLabel}
        </Button>
        <Button variant={variant} loading={pending} disabled={pending} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </AlertDialogActions>
    </AlertDialog>
  );
}
